import type { ReactNode } from 'react';
import { Dialog as BaseDialog } from '@base-ui/react/dialog';

import { Drawer } from '../Drawer';
import { Icon } from '../Icon';
import styles from './Dialog.module.css';

export interface DialogProps {
  children: ReactNode;
  className?: string;
  footer?: ReactNode;
  /** 居中弹窗标题下的辅助说明。 */
  description?: ReactNode;
  /** 居中弹窗表头配色；默认白色，选择类弹窗可显式使用主题色。 */
  headerTone?: 'standard' | 'brand';
  /** 置于既有抽屉等模态层之上的嵌套弹窗层级。 */
  modalLayer?: 'default' | 'nested';
  onOpenChange: (open: boolean) => void;
  open: boolean;
  side?: 'center' | 'right';
  /** 便捷档位；需要按场景定宽时改用 `width`。 */
  size?: 'sm' | 'md' | 'lg';
  title: string;
  /** 与 `size` 二选一：传入后按该宽度渲染（数字按 px，或任意 CSS 长度）。 */
  width?: number | string;
}

export function Dialog({ children, className, description, footer, headerTone = 'standard', modalLayer = 'default', onOpenChange, open, side = 'center', size = 'sm', title, width }: DialogProps) {
  /* 抽屉形态已由独立的 `Drawer` 组件承载（Figma 拆成两套组件），这里保留旧 API 并转发。 */
  if (side !== 'center') {
    return <Drawer className={className} footer={footer} onOpenChange={onOpenChange} open={open} title={title} width={width}>{children}</Drawer>;
  }

  const popupClass = [styles.popup, styles.center, width === undefined ? styles[size] : '', modalLayer === 'nested' ? styles.nestedPopup : '', className].filter(Boolean).join(' ');
  const backdropClass = [styles.backdrop, modalLayer === 'nested' ? styles.nestedBackdrop : ''].filter(Boolean).join(' ');
  const popupStyle = width === undefined ? undefined : { width: typeof width === 'number' ? `${width}px` : width };

  return (
    <BaseDialog.Root open={open} onOpenChange={onOpenChange}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop forceRender={modalLayer === 'nested'} className={backdropClass} data-modal-layer={modalLayer === 'nested' ? modalLayer : undefined} />
        <BaseDialog.Popup aria-modal="true" className={popupClass} data-header-tone={headerTone} data-modal-layer={modalLayer === 'nested' ? modalLayer : undefined} style={popupStyle}>
          <header className={styles.header}>
            {/* Atlassian Modal Dialog 规范：模态标题必须是 h1 级别标题。 */}
            <div className={styles.heading}>
              <BaseDialog.Title className={styles.title} render={<h1 />}>{title}</BaseDialog.Title>
              {description ? <BaseDialog.Description className={styles.description}>{description}</BaseDialog.Description> : null}
            </div>
            <BaseDialog.Close aria-label={`关闭${title}`} className={styles.close} data-icon="close">
              <Icon name="close" size="xs" />
            </BaseDialog.Close>
          </header>
          <div className={styles.body}>{children}</div>
          {footer ? <footer className={styles.footer}>{footer}</footer> : null}
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}
